/**
 * BLT / NMS + Court PDP — seamless colourway swap ("mini refresh").
 *
 * Tapping a colourway swatch on the new PDP is a plain <a href> — normally a
 * full page navigation (white flash / reload). This module intercepts those
 * taps and swaps the colourway IN PLACE: it fetches the sibling colourway's
 * PDP section (and the reviews section) with the theme's own Section Rendering
 * primitives, crossfade-morphs them in, updates the URL, and re-initialises the
 * PDP behaviour — header, footer and scroll position stay put, no reload.
 *
 * Progressive enhancement: this can never be WORSE than today. The swatches
 * stay real links; we only preventDefault when we can actually proceed, and on
 * ANY failure we fall back to a normal `location.href` navigation.
 *
 * How it stays bug-free across swaps:
 *   • The PDP body wrapper is product-keyed (id="blt-nmsp-grid-<product.id>"),
 *     so `morph` (which keys by id) REPLACES the whole grid subtree. Every
 *     custom element inside (blt-nms-variants, product-form-component,
 *     <strutfit-*>, wishlist buttons) re-connects fresh for the new product —
 *     no stale variant/price/product-code state.
 *   • The section's element-level listeners die with the old grid; the swap
 *     calls window.__bltNmspInit(root) to bind them on the fresh grid. The
 *     document/window listeners are registered once by the section and resolve
 *     the current grid dynamically, so they neither stack nor go stale.
 */

import { sectionRenderer, morphSection, normalizeSectionId } from '@theme/section-renderer';
import { startViewTransition } from '@theme/utilities';

(function () {
  if (window.__bltNmspSwapBound) return;
  window.__bltNmspSwapBound = true;

  var SWATCH_SEL = 'a.blt-nmsp__swatch[href]';
  var inFlight = false;

  /* --- section / element resolvers (always query live; DOM changes on swap) --- */
  function pdpSection() {
    return document.querySelector('[data-blt-nmsp]');
  }
  function sectionIdOf(el) {
    var wrapper = el && el.closest ? el.closest('.shopify-section') : null;
    return wrapper && wrapper.id ? normalizeSectionId(wrapper.id) : null;
  }
  function pdpSectionId() {
    return sectionIdOf(pdpSection());
  }
  function reviewsSectionId() {
    /* the forked brand reviews wall; if absent (e.g. Spectrum-owned section),
       skip the reviews morph — the PDP still swaps */
    var rv = document.querySelector('blt-nms-reviews');
    return rv ? sectionIdOf(rv) : null;
  }
  function gridId() {
    var g = document.querySelector('[data-blt-nmsp-grid]');
    return g ? g.id : null;
  }

  function reinit() {
    var root = pdpSection();
    if (root && typeof window.__bltNmspInit === 'function') window.__bltNmspInit(root);
    /* the reviews morph swaps in the new colorway's per-product server render;
       the wall element survives, so tell it to re-render from the line pool */
    var rv = document.querySelector('blt-nms-reviews');
    if (rv && typeof rv._postSwapRefresh === 'function') rv._postSwapRefresh();
  }

  /* --- the swap itself --------------------------------------------------- */
  function fullNav(href) {
    window.location.href = href;
  }

  function swap(href, push) {
    if (inFlight) return; /* drop taps while one is landing — fetches are fast/prefetched */
    var pdpId = pdpSectionId();
    if (!pdpId) {
      fullNav(href);
      return;
    }
    var revId = reviewsSectionId();
    var beforeGrid = gridId();
    inFlight = true;

    /* Scroll handling through the swap. The morph replaces the node the
       browser is scroll-anchored to and the reviews wall bounces through three
       heights — Chrome would re-anchor and teleport the page ~a viewport down.
       Freeze native anchoring and hold the offset THROUGH the morph frame, then
       glide to the top so the shopper sees the new colourway's gallery. */
    var anchorY = window.scrollY;
    document.documentElement.style.overflowAnchor = 'none';
    function unpinAnchoring() {
      document.documentElement.style.overflowAnchor = '';
    }
    function restoreScroll() {
      if (Math.abs(window.scrollY - anchorY) > 1) window.scrollTo(0, anchorY);
    }
    function scrollToGallery() {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }

    /* fetch FIRST (cache-hit instant if prefetched), THEN transition — snappier
       than fetching inside the transition. Fresh URL per call: getSectionHTML
       mutates the URL it is given (sets section_id / sorts params). */
    var reqs = [sectionRenderer.getSectionHTML(pdpId, true, new URL(href))];
    if (revId) reqs.push(sectionRenderer.getSectionHTML(revId, true, new URL(href)));

    Promise.all(reqs)
      .then(function (htmls) {
        var pdpHtml = htmls[0];
        var revHtml = revId ? htmls[1] : null;

        /* validate before touching the DOM — a soft-404 / redirect / password
           page would not contain our section wrapper */
        if (!pdpHtml || pdpHtml.indexOf('shopify-section-' + pdpId) === -1) {
          throw new Error('PDP section missing in response');
        }

        return startViewTransition(function () {
          try {
            morphSection(pdpId, pdpHtml);
          } catch (e) {
            /* swallow so the view-transition callback never throws (a throwing
               callback can hang the transition promise); verified after */
          }
          if (revId && revHtml) {
            try {
              morphSection(revId, revHtml);
            } catch (e) {
              /* reviews are non-critical; ignore */
            }
          }
        }, ['colorway-swap']);
      })
      .then(function () {
        /* success signal: the product-keyed grid was actually replaced */
        var afterGrid = gridId();
        if (!afterGrid || afterGrid === beforeGrid) {
          throw new Error('grid did not swap');
        }

        restoreScroll(); /* no teleport within the transition frame… */
        reinit();
        scrollToGallery(); /* …then show the new colourway's gallery */
        setTimeout(unpinAnchoring, 700);

        if (push) {
          try {
            history.pushState({ bltNmspSwap: true }, '', href);
          } catch (e) {
            /* ignore — the DOM already reflects the new colourway */
          }
        }

        /* best-effort tab title from the swapped-in DOM (kept in sync so
           back/forward reads right) */
        var g = document.querySelector('[data-blt-nmsp-grid]');
        var t = g && g.getAttribute('data-blt-nmsp-title');
        if (t) document.title = t;

        inFlight = false;
        prefetchSiblings(); /* new colourway → warm the new sibling set */
      })
      .catch(function () {
        unpinAnchoring();
        inFlight = false;
        fullNav(href); /* progressive-enhancement fallback: never worse than today */
      });
  }

  /* --- delegated swatch interception ------------------------------------ */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; /* new tab / etc. */
    var a = e.target.closest ? e.target.closest(SWATCH_SEL) : null;
    if (!a) return;
    if (a.target && a.target !== '_self') return; /* target="_blank" → let it be */
    if (!pdpSectionId()) return; /* no PDP context → normal navigation */
    var href = a.href;
    if (!href) return;
    e.preventDefault();
    swap(href, true);
  });

  /* --- back / forward ---------------------------------------------------- */
  window.addEventListener('popstate', function () {
    if (!pdpSectionId()) return;
    if (window.location.pathname.indexOf('/products/') === -1) return;
    swap(window.location.href, false); /* re-render for the current URL; no pushState */
  });

  /* --- prefetch: warm every sibling colourway's PDP section on idle so a tap
     is an instant cache hit (also the current URL, for instant back-nav) --- */
  var prefetched = {};
  function prefetchSiblings() {
    var pdpId = pdpSectionId();
    if (!pdpId) return;
    var hrefs = [window.location.href];
    var links = document.querySelectorAll(SWATCH_SEL);
    Array.prototype.forEach.call(links, function (a) {
      if (a.href) hrefs.push(a.href);
    });
    hrefs.forEach(function (href) {
      if (prefetched[href]) return;
      prefetched[href] = true;
      try {
        sectionRenderer.getSectionHTML(pdpId, true, new URL(href));
      } catch (e) {
        /* ignore prefetch failures — the real swap will fetch (or fall back) */
      }
    });
  }
  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 400); };
  if (document.readyState === 'complete') idle(prefetchSiblings);
  else window.addEventListener('load', function () { idle(prefetchSiblings); });
})();
